import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  Section, Inner, SectionHeader, SectionTitle, SectionSubtitle, Divider, Card, Badge,
} from '../styles/v2.styled';
import useLMSAuth, { isInstructor } from '../hooks/useLMSAuth';

const PageWrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.neutral[0]};
`;
const Hero = styled.section`
  padding: 70px 0 40px;
  background: linear-gradient(135deg, #0c4a6e, #0891b2);
  color: #fff; text-align: center;
`;
const HeroTitle = styled.h1`font-size: 2.2rem; margin: 0 0 12px; font-weight: 800;`;
const HeroSub = styled.p`font-size: 1rem; opacity: 0.85;`;

const TabBar = styled.div`
  display: flex; gap: 8px; margin-bottom: 24px; border-bottom: 1px solid #e5e7eb;
`;
const Tab = styled.button`
  padding: 12px 22px; border: none; background: none; font-size: 0.95rem; font-weight: 600;
  color: ${({ $active, theme }) => $active ? theme.colors.primary[600] : '#6b7280'};
  border-bottom: 2px solid ${({ $active, theme }) => $active ? theme.colors.primary[500] : 'transparent'};
  cursor: pointer;
`;
const Form = styled.div`background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 28px; margin-bottom: 28px;`;
const Field = styled.div`margin-bottom: 16px;`;
const Label = styled.label`display:block; font-size: 0.88rem; font-weight: 600; margin-bottom: 6px; color: #374151;`;
const Select = styled.select`width: 100%; padding: 12px 14px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 0.95rem; box-sizing: border-box;`;
const TextArea = styled.textarea`width: 100%; padding: 12px 14px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 0.95rem; box-sizing: border-box; min-height: 80px;`;
const PrimaryBtn = styled.button`
  padding: 12px 28px; font-size: 1rem; font-weight: 600; border-radius: 8px;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary[500]}, ${({ theme }) => theme.colors.primary[700]});
  color: #fff; border: none; cursor: pointer;
`;
const Table = styled.table`
  width: 100%; border-collapse: collapse;
  th, td { padding: 12px 14px; border-bottom: 1px solid #e5e7eb; text-align: left; font-size: 0.9rem; }
  th { background: #f9fafb; color: #374151; font-weight: 600; }
`;
const ActionBtn = styled.button`
  padding: 6px 12px; font-size: 0.78rem; border-radius: 6px;
  border: 1px solid ${({ $danger }) => $danger ? '#fecaca' : '#d1d5db'};
  background: ${({ $variant }) => $variant === 'approve' ? '#d1fae5' : $variant === 'reject' ? '#fee2e2' : '#fff'};
  color: ${({ $variant }) => $variant === 'approve' ? '#065f46' : $variant === 'reject' ? '#991b1b' : '#374151'};
  cursor: pointer; margin-right: 6px;
`;
const Empty = styled.div`text-align: center; padding: 40px; color: #6b7280; font-size: 0.95rem;`;

function statusBadge(s) {
  if (s === 'pending') return <Badge $variant="warning">대기</Badge>;
  if (s === 'approved') return <Badge $variant="success">승인</Badge>;
  if (s === 'rejected') return <Badge>반려</Badge>;
  return <Badge>{s}</Badge>;
}

export default function EnrollPage() {
  const { role, login, refresh } = useLMSAuth();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const presetCourse = params.get('course') || '';
  const presetTab = params.get('tab') === 'manage' ? 'manage' : 'apply';

  const [tab, setTab] = useState(presetTab);
  const [courses, setCourses] = useState([]);
  const [myEnrolls, setMyEnrolls] = useState([]);
  const [allEnrolls, setAllEnrolls] = useState([]);
  const [filter, setFilter] = useState('');

  const [form, setForm] = useState({ course_id: presetCourse, note: '' });

  const loadAll = async () => {
    const c = await fetch('/lms/courses').then((r) => r.json());
    setCourses(c);
    if (login) {
      const my = await fetch('/lms/enrollments/my', { credentials: 'include' });
      if (my.ok) setMyEnrolls(await my.json());
    }
    if (isInstructor(role)) {
      const all = await fetch('/lms/enrollments?status=' + filter, { credentials: 'include' });
      if (all.ok) setAllEnrolls(await all.json());
    }
  };
  useEffect(() => { loadAll(); }, [login, role, filter]);

  const apply = async () => {
    if (!login) { alert('로그인이 필요합니다.'); return; }
    if (!form.course_id) { alert('과정을 선택하세요.'); return; }
    const r = await fetch('/lms/enrollments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ course_id: Number(form.course_id), note: form.note }),
    });
    if (!r.ok) { alert('신청 실패: ' + (await r.text())); return; }
    alert('수강 신청 완료! 관리자 승인 후 [내 강의실]에서 시청할 수 있습니다.');
    setForm({ course_id: '', note: '' });
    setTab('mine');
    loadAll();
  };

  const process = async (id, action) => {
    const r = await fetch(`/lms/enrollments/${id}/${action}`, { method: 'PUT', credentials: 'include' });
    if (r.ok) loadAll(); else alert(action + ' 실패');
  };

  return (
    <PageWrapper>
      <Hero>
        <HeroTitle>수강 신청</HeroTitle>
        <HeroSub>원하는 과정을 신청하고 관리자 승인 후 강의를 들으세요</HeroSub>
      </Hero>

      <Section>
        <Inner>
          <TabBar>
            <Tab $active={tab === 'apply'} onClick={() => setTab('apply')}>신청하기</Tab>
            <Tab $active={tab === 'mine'} onClick={() => setTab('mine')}>내 신청 내역</Tab>
            {isInstructor(role) && (
              <Tab $active={tab === 'manage'} onClick={() => setTab('manage')}>신청 관리 (관리자)</Tab>
            )}
          </TabBar>

          {tab === 'apply' && (
            <Form>
              <SectionHeader $align="left" style={{ marginBottom: 20 }}>
                <Divider />
                <SectionTitle style={{ fontSize: '1.4rem' }}>새 수강 신청</SectionTitle>
              </SectionHeader>
              {!login && (
                <div style={{ background: '#fef3c7', padding: 14, borderRadius: 8, marginBottom: 16, color: '#92400e' }}>
                  로그인 후 수강 신청이 가능합니다. <Link to="/MyPage" style={{ fontWeight: 700 }}>로그인</Link>
                </div>
              )}
              <Field>
                <Label>과정 선택</Label>
                <Select value={form.course_id} onChange={(e) => setForm({ ...form, course_id: e.target.value })}>
                  <option value="">선택하세요</option>
                  {courses.filter((c) => c.status === 'open').map((c) => (
                    <option key={c.id} value={c.id}>{c.title} - {c.instructor_name || '강사 미정'}</option>
                  ))}
                </Select>
              </Field>
              <Field>
                <Label>신청 메모 (선택)</Label>
                <TextArea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder="신청 사유나 요청사항을 적어주세요" />
              </Field>
              <PrimaryBtn onClick={apply} disabled={!login}>신청</PrimaryBtn>
            </Form>
          )}

          {tab === 'mine' && (
            <Card style={{ padding: 0 }}>
              {myEnrolls.length === 0 ? (
                <Empty>신청 내역이 없습니다.</Empty>
              ) : (
                <Table>
                  <thead><tr><th>과정</th><th>강사</th><th>신청일</th><th>상태</th></tr></thead>
                  <tbody>
                    {myEnrolls.map((e) => (
                      <tr key={e.id}>
                        <td>{e.course_title}</td>
                        <td>{e.instructor_name || '-'}</td>
                        <td>{(e.applied_at || '').slice(0, 10)}</td>
                        <td>{statusBadge(e.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
          )}

          {tab === 'manage' && isInstructor(role) && (
            <>
              <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
                {['pending', 'approved', 'rejected', ''].map((s) => (
                  <ActionBtn key={s || 'all'} onClick={() => setFilter(s)}
                    style={{ background: filter === s ? '#0891b2' : '#fff', color: filter === s ? '#fff' : '#374151' }}>
                    {s === '' ? '전체' : s === 'pending' ? '대기' : s === 'approved' ? '승인' : '반려'}
                  </ActionBtn>
                ))}
              </div>
              <Card style={{ padding: 0 }}>
                {allEnrolls.length === 0 ? (
                  <Empty>신청 내역이 없습니다.</Empty>
                ) : (
                  <Table>
                    <thead>
                      <tr><th>과정</th><th>신청자</th><th>아이디</th><th>신청일</th><th>메모</th><th>상태</th><th>처리</th></tr>
                    </thead>
                    <tbody>
                      {allEnrolls.map((e) => (
                        <tr key={e.id}>
                          <td>{e.course_title}</td>
                          <td>{e.user_name || '-'}</td>
                          <td>{e.user_id}</td>
                          <td>{(e.applied_at || '').slice(0, 10)}</td>
                          <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.note}</td>
                          <td>{statusBadge(e.status)}</td>
                          <td>
                            {e.status === 'pending' && (
                              <>
                                <ActionBtn $variant="approve" onClick={() => process(e.id, 'approve')}>승인</ActionBtn>
                                <ActionBtn $variant="reject" onClick={() => process(e.id, 'reject')}>반려</ActionBtn>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card>
            </>
          )}
        </Inner>
      </Section>
    </PageWrapper>
  );
}
